<template>
  <transition name="page-loading-fade">
    <div
      v-if="internalVisible"
      class="page-loading-overlay"
      role="status"
      aria-live="polite"
    >
      <div class="page-loading-panel">
        <span class="page-loading-spinner" aria-hidden="true"></span>
        <div>
          <p class="page-loading-title">{{ title }}</p>
          <p v-if="message" class="page-loading-message">{{ message }}</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "PageLoadingOverlay",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Operazione in corso",
    },
    message: {
      type: String,
      default: "",
    },
    minVisibleMs: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      internalVisible: this.visible,
      visibleSince: this.visible ? Date.now() : null,
      hideTimer: null,
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.showOverlay();
        } else {
          this.hideOverlay();
        }
      },
    },
  },
  beforeDestroy() {
    this.clearHideTimer();
  },
  methods: {
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    showOverlay() {
      this.clearHideTimer();
      this.internalVisible = true;
      this.visibleSince = Date.now();
    },
    hideOverlay() {
      this.clearHideTimer();

      if (!this.internalVisible) return;

      const elapsed = this.visibleSince ? Date.now() - this.visibleSince : 0;
      const remaining = Math.max(this.minVisibleMs - elapsed, 0);

      if (remaining === 0) {
        this.internalVisible = false;
        this.visibleSince = null;
        return;
      }

      this.hideTimer = setTimeout(() => {
        this.internalVisible = false;
        this.visibleSince = null;
        this.hideTimer = null;
      }, remaining);
    },
  },
};
</script>

<style scoped>
.page-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(248, 249, 250, 0.68);
  backdrop-filter: blur(2px);
}

.page-loading-panel {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  width: min(26rem, 100%);
  padding: 1.1rem 1.25rem;
  color: #252a31;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(20, 26, 34, 0.18);
}

.page-loading-spinner {
  flex: 0 0 auto;
  width: 2.35rem;
  height: 2.35rem;
  border: 3px solid #dfe6ee;
  border-top-color: #4f7769;
  border-radius: 50%;
  animation: page-loading-spin 0.8s linear infinite;
}

.page-loading-title,
.page-loading-message {
  margin: 0;
}

.page-loading-title {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
}

.page-loading-message {
  margin-top: 0.25rem;
  color: #636c78;
  font-size: 0.9rem;
  line-height: 1.35;
}

.page-loading-fade-enter-active,
.page-loading-fade-leave-active {
  transition: opacity 0.18s ease;
}

.page-loading-fade-enter,
.page-loading-fade-leave-to {
  opacity: 0;
}

@keyframes page-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 520px) {
  .page-loading-panel {
    align-items: flex-start;
  }
}
</style>
